import { inject, Injectable } from '@angular/core'
import { _stringify } from '@naturalcycles/js-lib'
import {
  AccountChangeHardwareInput,
  AccountPatchInput,
  BackendResponseFM,
  BackendResponseFMResp,
  EmailInput,
  Goal,
  HardwareId,
  LoginOrRegisterInput,
  LoginProvider,
  normalizeLanguage,
  ResetPasswordCompleteInput,
  ResetPasswordTokenInput,
  ShouldLoginInput,
  SocialAuthInput,
  VerifyCredentialsInput,
  VerifyCredentialsResponse,
} from '@naturalcycles/shared'
import { AlertComponent } from '@src/app/components/modals/alert.component.js'
import { LinkAccountComponent } from '@src/app/components/modals/link-account.component.js'
import { MixpanelService } from '@src/app/core/services/analytics/mixpanel.service.js'
import { api } from '@src/app/core/services/api.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { MixpanelEvent } from '@src/app/shared/typings/analytics.js'
import { SocialAuthProviderResult } from '@src/app/shared/typings/interfaces/user-auth.js'
import { PasswordLengthIndicator } from '@src/app/shared/typings/ui.js'
import { CartService } from './cart.service.js'
import { ModalService } from './modal.service.js'
import { sessionSigningService } from './sessionSigning.service.js'

export const MIN_LOGIN_PASSWORD_LENGTH = 6
export const MIN_NEW_PASSWORD_LENGTH = 8
@Injectable({ providedIn: 'root' })
export class AccountService {
  private mixpanelService = inject(MixpanelService)
  private modalService = inject(ModalService)
  shouldLoginRequests: { [email: string]: Promise<boolean> } = {}
  verifyEmailRequests: { [email: string]: Promise<boolean> } = {}

  private store = inject(SignalStore)
  private cartService = inject(CartService)

  async refreshResetPasswordToken(json: ResetPasswordTokenInput): Promise<void> {
    await api.post('accounts/resetPassword/refresh', { json })
  }

  async completeResetPassword(json: ResetPasswordCompleteInput): Promise<BackendResponseFM> {
    const { backendResponse } = await api.post<BackendResponseFMResp>(
      'accounts/resetPassword/complete',
      { json },
    )
    return backendResponse
  }

  async login(json: LoginOrRegisterInput): Promise<BackendResponseFM> {
    const { backendResponse } = await api.post<BackendResponseFMResp>('sessions', { json })
    return backendResponse
  }

  async createOrLogin(json: LoginOrRegisterInput): Promise<BackendResponseFM> {
    const { backendResponse } = await api.post<BackendResponseFMResp>('sessions/auto', { json })
    return backendResponse
  }

  async patch(json: AccountPatchInput): Promise<BackendResponseFM> {
    const { backendResponse } = await api.patch<BackendResponseFMResp>('accounts/my', { json })
    return backendResponse
  }

  async shouldLoginUser(input: ShouldLoginInput): Promise<boolean> {
    return await api
      .put<BackendResponseFMResp>('accounts/shouldLogin', {
        json: input,
      })
      .then(r => r.backendResponse.res)
  }

  async verifyCredentials(email: string, pw: string): Promise<VerifyCredentialsResponse> {
    const json = { email, pw } as VerifyCredentialsInput
    return await api
      .put<BackendResponseFMResp>(`accounts/verifyCredentials`, { json })
      .then(r => r.backendResponse.res)
  }

  async changeEmail(email: string): Promise<void> {
    const json: EmailInput = { email }
    await api.post('accounts/changeEmail', { json })
  }

  async changePassword(pwOld: string, pwNew: string): Promise<void> {
    await api.post('accounts/changePw', {
      json: { pwOld, pwNew },
    })
  }

  async sendVerificationEmail(): Promise<void> {
    await api.get('accounts/sendVerificationEmail')
  }

  async resetPassword(json: EmailInput): Promise<void> {
    await api.post('accounts/resetPassword', { json })
  }

  async resetPasswordModal(email: string): Promise<void> {
    const body: EmailInput = { email }
    await this.resetPassword(body)

    return await this.modalService.show({
      component: AlertComponent,
      modalName: 'ResetPasswordModal',
      titleKey: 'reset-password-title',
      api: {
        message: 'reset-password-email-sent',
      },
    })
  }

  getPasswordLengthIndicator(pw: string): PasswordLengthIndicator {
    if (pw.length <= 7) {
      return PasswordLengthIndicator.WEAK
    }
    if (pw.length <= 11) {
      return PasswordLengthIndicator.MEDIUM
    }
    return PasswordLengthIndicator.STRONG
  }

  async saveGoal(goal: Goal): Promise<void> {
    void this.mixpanelService.trackEvent(MixpanelEvent.GoalSelected, { goal: Goal[goal] })

    this.store.$account.update(s => ({ ...s, goal }))
  }

  async socialAuth(result: SocialAuthProviderResult): Promise<BackendResponseFM> {
    const { quiz, lang, cart, account, discountCode } = this.store.getState()

    const input: SocialAuthInput = {
      id: result.input.id,
      email: result.input.email || '',
      pw: result.input.pw,
      token: result.input.token,
      name1: result.input.name,
      name2: result.input.lastName,
      plan: this.cartService.getSubscriptionType(),
      lang: normalizeLanguage(lang),
      goal: account.goal,
      hwId: cart.hwId,
      marketingConsent: result.consent?.marketingConsent,
      marketingSocialConsent: result.consent?.marketingSocialConsent,
      appConsent: !!result.consent,
      allowToCreateNewAccount: result.input.allowToCreateNewAccount,
      quizData: quiz.data || undefined,
      publicKey: await sessionSigningService.getPublicKey(),
      discountCode: discountCode?.code,
    }

    const response = await (result.input.loginProvider === LoginProvider.APPLE
      ? this.appleAuth(input)
      : this.googleAuth(input))

    return response
  }

  async appleAuth(json: SocialAuthInput): Promise<BackendResponseFM> {
    return await api
      .put<BackendResponseFMResp>(`sessions/appleAuth`, { json })
      .then(r => r.backendResponse)
  }

  async googleAuth(json: SocialAuthInput): Promise<BackendResponseFM> {
    return await api
      .put<BackendResponseFMResp>(`sessions/googleAuth`, { json })
      .then(r => r.backendResponse)
  }

  public async linkAccountWithPassword(
    loginProvider: LoginProvider,
    email?: string,
  ): Promise<string> {
    let password: any = ''
    await this.modalService.show({
      component: LinkAccountComponent,
      titleKey: 'auth-existing-account-header',
      api: {
        message:
          loginProvider === LoginProvider.APPLE
            ? 'auth-apple-existing-account-message'
            : 'auth-google-existing-account-message',
        getAccountPassword: (pw: string) => {
          password = pw
          this.modalService.remove()
        },
        forgotPassword: async (forgotPassword: boolean) => {
          if (email && forgotPassword) {
            await this.resetPasswordModal(email)
          }
          this.modalService.remove()
        },
      },
      skipControls: true,
    })

    return password
  }

  async errorLinkingAccount(error: Error, email: string): Promise<void> {
    if (_stringify(error).includes('The password is not correct')) {
      throw error
    }
    await this.resetPasswordModal(email)
  }

  async exitDemo(): Promise<void> {
    await api.post<BackendResponseFMResp>(`accounts/demoModeExit`)
  }

  async changeHardwareIdIfIncomplete(hwId: HardwareId): Promise<BackendResponseFM | null> {
    const { completeDate } = this.store.$account()
    if (completeDate) return null
    return await api
      .put<BackendResponseFMResp>('accounts/changeHardware', {
        json: { hwId } satisfies AccountChangeHardwareInput,
      })
      .then(r => r.backendResponse)
  }

  isLoggedIn(): boolean {
    const { id } = this.store.$account()
    const sessionId = this.store.$sessionId()
    return !!id && !!sessionId
  }
}
